import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { useSettings } from '@/hooks/useProducts';

/**
 * Stripe.js for the storefront.
 *
 * The publishable key used to be BUILD-TIME only (VITE_STRIPE_PUBLISHABLE_KEY,
 * written into the tenant's .env by the site wizard). A self-serve tenant
 * never had it — the wizard shipped a "pk_test_..." placeholder — and the
 * keys they paste at /admin/payments live in the database, so no rebuild
 * ever picked them up: Elements mounted with a dead key and no card could be
 * taken. `/api/settings/public` now serves `stripe_publishable_key`
 * (credential_overrides → env) and `useStripePromise` prefers it, falling
 * back to the build-time key so tenants configured via env are unchanged.
 */

const buildTimeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

/** Build-time key only. Kept for code that runs outside React. */
export const stripePromise = buildTimeKey ? loadStripe(buildTimeKey) : null;

const promisesByKey = new Map<string, Promise<Stripe | null>>();

/** loadStripe once per key (loadStripe itself injects the script once). */
export function stripePromiseForKey(key: string | null | undefined): Promise<Stripe | null> | null {
  const k = (key || '').trim();
  if (!k) return null;
  let p = promisesByKey.get(k);
  if (!p) {
    p = loadStripe(k);
    promisesByKey.set(k, p);
  }
  return p;
}

/**
 * The Stripe promise for THIS tenant: runtime key from public settings first,
 * build-time key second, null when neither exists (payments not configured).
 * Returns null while settings are still loading only if there is no
 * build-time fallback either.
 */
export function useStripePromise(): Promise<Stripe | null> | null {
  const { data } = useSettings();
  const runtimeKey = data?.settings?.stripe_publishable_key as string | undefined;
  return stripePromiseForKey(runtimeKey) ?? stripePromise;
}
